import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";

const STORAGE_KEY = "ruempeltaxi_consent_v1";

export function getConsent() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ statistics: false, media: false });

  useEffect(() => {
    const existing = getConsent();
    if (!existing) setOpen(true);
    const handler = () => {
      const c = getConsent();
      if (c) setPrefs({ statistics: !!c.statistics, media: !!c.media });
      setShowSettings(true);
      setOpen(true);
    };
    window.addEventListener("open-cookie-settings", handler);
    return () => window.removeEventListener("open-cookie-settings", handler);
  }, []);

  const save = useCallback((value) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...value, necessary: true, ts: Date.now() })
    );
    setOpen(false);
    setShowSettings(false);
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-2xl rounded-md border border-dark-gray bg-anthracite p-6 shadow-2xl md:inset-x-6 xl:bottom-6"
          role="dialog"
          aria-label="Cookie-Einstellungen"
          data-testid="cookie-banner"
        >
          <h2 className="font-heading text-lg font-bold text-white">Datenschutz &amp; Cookies</h2>
          <p className="mt-2 text-sm leading-relaxed text-light-gray/70">
            Wir verwenden nur technisch notwendige Cookies, um die Website bereitzustellen.
            Optionale Dienste (Statistik, externe Medien) werden ausschließlich nach Ihrer
            Einwilligung geladen. Details finden Sie in unserer{" "}
            <Link to="/datenschutz" className="text-yellow underline">
              Datenschutzerklärung
            </Link>
            .
          </p>

          <AnimatePresence>
            {showSettings && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 space-y-3 overflow-hidden"
              >
                <label className="flex items-start gap-3 rounded-sm bg-dark-gray/40 p-3">
                  <input type="checkbox" checked disabled className="mt-1 accent-yellow" />
                  <span className="text-sm text-light-gray/80">
                    <strong className="text-white">Technisch erforderlich</strong> – immer aktiv.
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-sm bg-dark-gray/40 p-3">
                  <input
                    type="checkbox"
                    checked={prefs.statistics}
                    onChange={(e) => setPrefs((p) => ({ ...p, statistics: e.target.checked }))}
                    className="mt-1 accent-yellow"
                    data-testid="consent-statistics"
                  />
                  <span className="text-sm text-light-gray/80">
                    <strong className="text-white">Statistik</strong> – anonyme Nutzungsanalyse.
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-sm bg-dark-gray/40 p-3">
                  <input
                    type="checkbox"
                    checked={prefs.media}
                    onChange={(e) => setPrefs((p) => ({ ...p, media: e.target.checked }))}
                    className="mt-1 accent-yellow"
                    data-testid="consent-media"
                  />
                  <span className="text-sm text-light-gray/80">
                    <strong className="text-white">Externe Medien</strong> – z. B. Kartendienste.
                  </span>
                </label>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <button
              onClick={() => save({ statistics: true, media: true })}
              className="flex-1 rounded-sm bg-yellow px-4 py-3 font-heading text-sm font-bold text-brand-black transition-colors hover:bg-yellow-dark"
              data-testid="consent-accept-all"
            >
              Alle akzeptieren
            </button>
            <button
              onClick={() =>
                showSettings
                  ? save(prefs)
                  : save({ statistics: false, media: false })
              }
              className="flex-1 rounded-sm border border-dark-gray bg-transparent px-4 py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-dark-gray"
              data-testid="consent-necessary"
            >
              {showSettings ? "Auswahl speichern" : "Nur notwendige"}
            </button>
            {!showSettings && (
              <button
                onClick={() => setShowSettings(true)}
                className="rounded-sm px-4 py-3 font-heading text-sm font-semibold text-light-gray/70 transition-colors hover:text-white"
                data-testid="consent-settings"
              >
                Einstellungen
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
